import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptTaskSuggestion,
  attachMeetingTranscript,
  createMeeting,
  dismissTaskSuggestion,
  generateTaskSuggestions,
  generateMeetingMinutes,
  listMeetingMinutes,
  saveMeetingMinutesToDrive,
  emailMeetingMinutes,
  type MeetingMinutes,
  listTaskSuggestions,
  restoreTaskSuggestion,
  type AcceptTaskSuggestionInput,
  createMeetingContact,
  deleteMeeting,
  deleteMeetingContact,
  getMeeting,
  getMeetingContact,
  listMeetingContacts,
  listMeetings,
  updateMeeting,
  requestMeetingAccess,
  grantMeetingAccess,
  revokeMeetingAccess,
  type MeetingAccessLevel,
  updateMeetingContact,
  type ContactFilters,
  type MeetingContactInput,
  type MeetingFilters,
  type MeetingInput,
} from '../lib/meetingLogApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

// Meetings module (Întâlniri) hooks. The global firm switcher is threaded into
// list queries like the clients/projects hooks do; an explicit companyId wins.

const MEETINGS_KEY = ['meetings'] as const;
const CONTACTS_KEY = ['meeting-contacts'] as const;

export function useMeetings(filters: MeetingFilters = {}, options: { enabled?: boolean } = {}) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: MeetingFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'list', merged],
    queryFn: () => listMeetings(merged),
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'detail', id],
    queryFn: () => getMeeting(id as string),
    enabled: Boolean(id),
    // A freshly attached transcript walks PENDING -> COMPLETED in the background;
    // keep the card's AI summary current while that happens.
    refetchInterval: (query) => {
      const m = query.state.data;
      if (!m) return false;
      const active = m.meetingNotes.some(
        (n) => n.ingestStatus === 'PENDING' || n.ingestStatus === 'PROCESSING',
      );
      return active ? 5000 : false;
    },
  });
}

function invalidateMeetings(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, 'list'] });
  if (id) {
    void qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, 'detail', id] });
    // A project change re-points the pending task suggestions too.
    void qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, 'task-suggestions', id] });
  }
  // Inline-created contacts land in the directory too.
  void qc.invalidateQueries({ queryKey: CONTACTS_KEY });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingInput) => createMeeting(input),
    onSuccess: () => invalidateMeetings(qc),
  });
}

export function useUpdateMeeting(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Omit<MeetingInput, 'transcript'>>) => updateMeeting(id, patch),
    onSuccess: () => invalidateMeetings(qc, id),
  });
}

export function useRequestMeetingAccess(id: string) {
  return useMutation({ mutationFn: () => requestMeetingAccess(id) });
}

export function useGrantMeetingAccess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; level: MeetingAccessLevel }) =>
      grantMeetingAccess(id, v.userId, v.level),
    onSuccess: () => invalidateMeetings(qc, id),
  });
}

export function useRevokeMeetingAccess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => revokeMeetingAccess(id, userId),
    onSuccess: () => invalidateMeetings(qc, id),
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSuccess: () => invalidateMeetings(qc),
  });
}

export function useAttachMeetingTranscript(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (transcript: string) => attachMeetingTranscript(id, transcript),
    onSuccess: () => {
      invalidateMeetings(qc, id);
      void qc.invalidateQueries({ queryKey: ['meeting-notes', `meeting:${id}`] });
    },
  });
}

// --- Task suggestions -------------------------------------------------------------------

const SUGGESTIONS_KEY = (meetingId: string) => [...MEETINGS_KEY, 'task-suggestions', meetingId];

/**
 * The AI's proposed tasks for a meeting. `polling` should be true while a
 * transcript is still being processed: the suggestions land at the end of
 * that pipeline, so the list is refreshed until they show up.
 */
export function useMeetingTaskSuggestions(meetingId: string | undefined, polling = false) {
  return useQuery({
    queryKey: SUGGESTIONS_KEY(meetingId ?? ''),
    queryFn: () => listTaskSuggestions(meetingId as string),
    enabled: Boolean(meetingId),
    refetchInterval: polling ? 5000 : false,
  });
}

function invalidateSuggestions(qc: ReturnType<typeof useQueryClient>, meetingId: string) {
  void qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY(meetingId) });
}

export function useGenerateTaskSuggestions(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId?: string) => generateTaskSuggestions(meetingId, noteId),
    onSuccess: () => invalidateSuggestions(qc, meetingId),
  });
}

export function useAcceptTaskSuggestion(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AcceptTaskSuggestionInput }) =>
      acceptTaskSuggestion(meetingId, id, input),
    onSuccess: () => {
      invalidateSuggestions(qc, meetingId);
      // A real task now exists on some project.
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDismissTaskSuggestion(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissTaskSuggestion(meetingId, id),
    onSuccess: () => invalidateSuggestions(qc, meetingId),
  });
}

export function useRestoreTaskSuggestion(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreTaskSuggestion(meetingId, id),
    onSuccess: () => invalidateSuggestions(qc, meetingId),
  });
}

// --- Contacts ---------------------------------------------------------------------------

export function useMeetingContacts(filters: ContactFilters = {}, enabled = true) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: ContactFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'list', merged],
    queryFn: () => listMeetingContacts(merged),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useMeetingContact(id: string | undefined) {
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'detail', id],
    queryFn: () => getMeetingContact(id as string),
    enabled: Boolean(id),
  });
}

function invalidateContacts(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: CONTACTS_KEY });
  // Contact labels are embedded in meeting rows.
  void qc.invalidateQueries({ queryKey: MEETINGS_KEY });
}

export function useCreateMeetingContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingContactInput) => createMeetingContact(input),
    onSuccess: () => invalidateContacts(qc),
  });
}

export function useUpdateMeetingContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<MeetingContactInput> }) =>
      updateMeetingContact(id, patch),
    onSuccess: () => invalidateContacts(qc),
  });
}

export function useDeleteMeetingContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeetingContact(id),
    onSuccess: () => invalidateContacts(qc),
  });
}

// --- Minutes ----------------------------------------------------------------------

const MINUTES_KEY = (meetingId: string) => [...MEETINGS_KEY, 'minutes', meetingId];

/**
 * The minutes documents written for a meeting, newest first. `polling`
 * should be true while a transcript is still being processed: a new
 * meeting's minutes are written at the end of that pipeline.
 */
export function useMeetingMinutes(meetingId: string | undefined, polling = false) {
  return useQuery({
    queryKey: MINUTES_KEY(meetingId ?? ''),
    queryFn: () => listMeetingMinutes(meetingId as string),
    enabled: Boolean(meetingId),
    refetchInterval: polling ? 5000 : false,
  });
}

/** Shared by every „Generează minuta” button on the page, so they all show the same pending state. */
export const GENERATE_MINUTES_KEY = (meetingId: string) => ['meeting-minutes-generate', meetingId];

export function useGenerateMeetingMinutes(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: GENERATE_MINUTES_KEY(meetingId),
    mutationFn: () => generateMeetingMinutes(meetingId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MINUTES_KEY(meetingId) });
    },
  });
}

export function useSaveMeetingMinutesToDrive(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: MeetingMinutes) => saveMeetingMinutesToDrive(meetingId, m),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MINUTES_KEY(meetingId) });
    },
  });
}

export function useEmailMeetingMinutes(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: MeetingMinutes) => emailMeetingMinutes(meetingId, m),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MINUTES_KEY(meetingId) });
    },
  });
}

/** True while any button on the page is generating this meeting's minutes. */
export function useGeneratingMeetingMinutes(meetingId: string): boolean {
  return useIsMutating({ mutationKey: GENERATE_MINUTES_KEY(meetingId) }) > 0;
}
